// src/components/manager/shared/ResourcesFormFields.jsx
import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit, Trash2, Search, Filter, MapPin, User, X,
  Monitor, Car, Armchair, Smartphone, Printer, Wrench, RefreshCw,
  CheckCircle, AlertCircle, Clock, ArrowRight, History, QrCode,
  BarChart3, TrendingUp, Calendar, Building2
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';
import { organisationService } from '../../../services/organisationService';

const RESOURCE_TYPES = {
  computer: { label: 'Computers', color: 'blue', icon: Monitor },
  furniture: { label: 'Furniture', color: 'green', icon: Armchair },
  vehicle: { label: 'Vehicles', color: 'purple', icon: Car },
  device: { label: 'Devices', color: 'indigo', icon: Smartphone },
  equipment: { label: 'Equipment', color: 'orange', icon: Printer },
  tool: { label: 'Tools', color: 'red', icon: Wrench },
  other: { label: 'Other', color: 'gray', icon: Package }
};

const RESOURCE_STATUS = {
  available: { label: 'Available', color: 'green', icon: CheckCircle },
  allocated: { label: 'Allocated', color: 'blue', icon: User },
  maintenance: { label: 'Maintenance', color: 'yellow', icon: Wrench },
  retired: { label: 'Retired', color: 'red', icon: AlertCircle },
  lost: { label: 'Lost/Stolen', color: 'red', icon: AlertCircle }
};

const CONDITION_LEVELS = {
  excellent: { label: 'Excellent', color: 'green' },
  good: { label: 'Good', color: 'blue' },
  fair: { label: 'Fair', color: 'yellow' },
  poor: { label: 'Poor', color: 'red' }
};

export const ResourcesFormFields = ({ bookId, templateType, onUpdate, accessLevel = 'internal' }) => {
  const [resources, setResources] = useState([]);
  const [sites, setSites] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [allocatingResource, setAllocatingResource] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'computer',
    status: 'available',
    condition: 'good',
    serialNumber: '',
    assetTag: '',
    brand: '',
    model: '',
    specifications: '',
    purchaseDate: '',
    purchasePrice: '',
    currentValue: '',
    warranty: '',
    currentSite: '',
    allocatedTo: '',
    allocatedBy: '',
    allocationDate: '',
    allocationNotes: '',
    maintenanceSchedule: 'annually',
    lastMaintenance: '',
    nextMaintenance: '',
    images: [],
    documents: [],
    notes: '',
    customFields: {}
  });

  const [allocationData, setAllocationData] = useState({
    allocatedTo: '',
    allocatedBy: '',
    newSite: '',
    allocationDate: new Date().toISOString().split('T')[0],
    allocationNotes: '',
    returnDate: '',
    allocationType: 'permanent' // permanent, temporary, loan
  });

  useEffect(() => {
    loadResourceData();
    loadRelatedData();
  }, [bookId]);

  const loadResourceData = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getManagementFeatureData(bookId, 'resources');
      setResources(data?.resources || []);
    } catch (error) {
      console.error('Error loading resource data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedData = async () => {
    try {
      // Load sites
      const siteData = await organisationService.getManagementFeatureData(bookId, 'sites');
      setSites(siteData?.sites || []);
      
      // Load team members
      const teamData = await organisationService.getManagementFeatureData(bookId, 'team');
      setTeamMembers(teamData?.members || []);
    } catch (error) {
      console.error('Error loading related data:', error);
    }
  };

  const saveResourceData = async (updatedResources) => {
    try {
      const stats = {
        totalResources: updatedResources.length,
        availableResources: updatedResources.filter(r => r.status === 'available').length,
        allocatedResources: updatedResources.filter(r => r.status === 'allocated').length,
        maintenanceRequired: updatedResources.filter(r => r.status === 'maintenance').length,
        totalValue: updatedResources.reduce((sum, resource) => sum + (parseFloat(resource.currentValue) || 0), 0)
      };

      await organisationService.updateManagementFeatureData(bookId, 'resources', {
        resources: updatedResources,
        lastUpdated: new Date(),
        ...stats
      });

      // Log the activity
      await logActivity('resource', 'updated', { 
        resourceCount: updatedResources.length,
        timestamp: new Date()
      });

      onUpdate?.();
    } catch (error) {
      console.error('Error saving resource data:', error);
      throw error;
    }
  };

  const logActivity = async (entityType, action, details) => {
    try {
      const activity = {
        id: Date.now().toString(),
        entityType,
        action,
        details,
        timestamp: new Date(),
        user: 'System' // In production, this would come from auth context
      };

      // Add to records management
      const recordsData = await organisationService.getManagementFeatureData(bookId, 'records');
      const activities = recordsData?.activities || [];
      activities.unshift(activity);

      await organisationService.updateManagementFeatureData(bookId, 'records', {
        activities: activities.slice(0, 1000), // Keep last 1000 activities
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const resourceData = {
        ...formData,
        id: editingResource?.id || Date.now().toString(),
        createdAt: editingResource?.createdAt || new Date(),
        updatedAt: new Date(),
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : 0,
        currentValue: formData.currentValue ? parseFloat(formData.currentValue) : 0,
        allocationHistory: editingResource?.allocationHistory || []
      };

      let updatedResources;
      if (editingResource) {
        updatedResources = resources.map(resource => 
          resource.id === editingResource.id ? resourceData : resource
        );
        await logActivity('resource', 'updated', { 
          resourceId: resourceData.id, 
          resourceName: resourceData.name 
        });
      } else {
        updatedResources = [...resources, resourceData];
        await logActivity('resource', 'created', { 
          resourceId: resourceData.id, 
          resourceName: resourceData.name 
        });
      }

      await saveResourceData(updatedResources);
      setResources(updatedResources);
      handleCloseForm();
    } catch (error) {
      alert('Error saving resource: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const allocation = {
        ...allocationData,
        id: Date.now().toString(),
        timestamp: new Date()
      };

      const updatedResources = resources.map(resource => {
        if (resource.id === allocatingResource.id) {
          return {
            ...resource,
            status: 'allocated',
            allocatedTo: allocation.allocatedTo,
            allocatedBy: allocation.allocatedBy,
            currentSite: allocation.newSite || resource.currentSite,
            allocationDate: allocation.allocationDate,
            allocationNotes: allocation.allocationNotes,
            updatedAt: new Date(),
            allocationHistory: [
              ...(resource.allocationHistory || []),
              allocation
            ]
          };
        }
        return resource;
      });

      await saveResourceData(updatedResources);
      setResources(updatedResources);

      await logActivity('resource', 'allocated', {
        resourceId: allocatingResource.id,
        resourceName: allocatingResource.name,
        allocatedTo: allocation.allocatedTo,
        allocatedBy: allocation.allocatedBy
      });

      handleCloseAllocationForm();
    } catch (error) {
      alert('Error allocating resource: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeallocate = async (resource) => {
    if (!window.confirm(`Deallocate ${resource.name} from ${resource.allocatedTo}?`)) return;

    try {
      const deallocation = {
        id: Date.now().toString(),
        deallocatedFrom: resource.allocatedTo,
        deallocatedBy: 'System',
        deallocationDate: new Date().toISOString().split('T')[0],
        timestamp: new Date()
      };

      const updatedResources = resources.map(r => {
        if (r.id === resource.id) {
          return {
            ...r,
            status: 'available',
            allocatedTo: '',
            allocatedBy: '',
            allocationDate: '',
            allocationNotes: '',
            updatedAt: new Date(),
            allocationHistory: [
              ...(r.allocationHistory || []),
              deallocation
            ]
          };
        }
        return r;
      });

      await saveResourceData(updatedResources);
      setResources(updatedResources);

      await logActivity('resource', 'deallocated', {
        resourceId: resource.id,
        resourceName: resource.name,
        deallocatedFrom: resource.allocatedTo
      });
    } catch (error) {
      alert('Error deallocating resource: ' + error.message);
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      const resource = resources.find(r => r.id === resourceId);
      const updatedResources = resources.filter(resource => resource.id !== resourceId);
      await saveResourceData(updatedResources);
      setResources(updatedResources);

      await logActivity('resource', 'deleted', {
        resourceId,
        resourceName: resource.name
      });
    } catch (error) {
      alert('Error deleting resource: ' + error.message);
    }
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setFormData(resource);
    setShowForm(true);
  };

  const handleShowAllocation = (resource) => {
    setAllocatingResource(resource);
    setAllocationData({
      allocatedTo: '', allocatedBy: '', newSite: resource.currentSite || '',
      allocationDate: new Date().toISOString().split('T')[0],
      allocationNotes: '', returnDate: '', allocationType: 'permanent'
    });
    setShowAllocationForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingResource(null);
    setFormData({
      name: '', type: 'computer', status: 'available', condition: 'good',
      serialNumber: '', assetTag: '', brand: '', model: '', specifications: '',
      purchaseDate: '', purchasePrice: '', currentValue: '', warranty: '',
      currentSite: '', allocatedTo: '', allocatedBy: '', allocationDate: '',
      allocationNotes: '', maintenanceSchedule: 'annually', lastMaintenance: '',
      nextMaintenance: '', images: [], documents: [], notes: '', customFields: {}
    });
  };

  const handleCloseAllocationForm = () => {
    setShowAllocationForm(false);
    setAllocatingResource(null);
    setAllocationData({
      allocatedTo: '', allocatedBy: '', newSite: '', 
      allocationDate: new Date().toISOString().split('T')[0],
      allocationNotes: '', returnDate: '', allocationType: 'permanent'
    });
  };

  const handleImageUpload = async (files, error) => {
    if (error) {
      alert('Error uploading images: ' + error);
      return;
    }

    try {
      const results = await firestoreService.uploadMultipleFiles(
        Array.isArray(files) ? files : [files],
        (file) => firestoreService.uploadFile(file, `pb-${bookId}/resources/images`),
        (current, total, result, error) => {
          if (error) console.error('Upload error:', error);
        }
      );

      const successfulUploads = results.filter(r => r.success !== false);
      const newImages = successfulUploads.map(result => ({
        url: result.url,
        fileName: result.fileName,
        uploadedAt: new Date()
      }));

      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages]
      }));
    } catch (error) {
      alert('Error uploading images: ' + error.message);
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.assetTag.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.allocatedTo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || resource.type === filterType;
    const matchesStatus = filterStatus === 'all' || resource.status === filterStatus;
    const matchesSite = filterSite === 'all' || resource.currentSite === filterSite;
    
    return matchesSearch && matchesType && matchesStatus && matchesSite;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="h-7 w-7 text-blue-600" />
            Resources Management
          </h3>
          <p className="text-gray-600 mt-1">Track and allocate distributable resources across sites</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Resource
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Resources</p>
              <p className="text-2xl font-bold text-gray-900">{resources.length}</p>
            </div>
            <Package className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Available</p>
              <p className="text-2xl font-bold text-green-600">
                {resources.filter(r => r.status === 'available').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Allocated</p>
              <p className="text-2xl font-bold text-blue-600">
                {resources.filter(r => r.status === 'allocated').length}
              </p>
            </div>
            <User className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Maintenance</p>
              <p className="text-2xl font-bold text-yellow-600">
                {resources.filter(r => r.status === 'maintenance').length}
              </p>
            </div>
            <Wrench className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-purple-600">
                R{resources.reduce((sum, resource) => sum + (parseFloat(resource.currentValue) || 0), 0).toLocaleString()}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {Object.entries(RESOURCE_TYPES).map(([key, type]) => (
            <option key={key} value={key}>{type.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {Object.entries(RESOURCE_STATUS).map(([key, status]) => (
            <option key={key} value={key}>{status.label}</option>
          ))}
        </select>

        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Sites</option>
          {sites.map(site => (
            <option key={site.id} value={site.name}>{site.name}</option>
          ))}
        </select>
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources.map(resource => {
          const typeConfig = RESOURCE_TYPES[resource.type] || RESOURCE_TYPES.other;
          const statusConfig = RESOURCE_STATUS[resource.status] || RESOURCE_STATUS.available;
          const conditionConfig = CONDITION_LEVELS[resource.condition] || CONDITION_LEVELS.good;
          const TypeIcon = typeConfig.icon;
          const StatusIcon = statusConfig.icon;
          
          return (
            <div key={resource.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Resource Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${typeConfig.color}-100`}>
                    <TypeIcon className={`h-5 w-5 text-${typeConfig.color}-600`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{resource.name}</h4>
                    <p className="text-sm text-gray-600">{resource.brand} {resource.model}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full bg-${conditionConfig.color}-500`} title={`${conditionConfig.label} condition`} />
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                    <StatusIcon className="h-3 w-3" />
                    {statusConfig.label}
                  </div>
                </div>
              </div>

              {/* Resource Image */}
              {resource.images && resource.images.length > 0 && (
                <div className="mb-4">
                  <img 
                    src={resource.images[0].url} 
                    alt={resource.name}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Resource Details */}
              <div className="space-y-2 mb-4">
                {resource.assetTag && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <QrCode className="h-4 w-4" />
                    {resource.assetTag}
                  </div>
                )}

                {resource.serialNumber && (
                  <p className="text-sm text-gray-600">S/N: {resource.serialNumber}</p>
                )}

                {resource.currentSite && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    {resource.currentSite}
                  </div>
                )}

                {resource.allocatedTo && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    Allocated to: {resource.allocatedTo}
                  </div>
                )}

                {resource.currentValue && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <BarChart3 className="h-4 w-4" />
                    Value: R{parseFloat(resource.currentValue).toLocaleString()}
                  </div>
                )}
              </div>

              {/* Allocation History */}
              {resource.allocationHistory && resource.allocationHistory.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <History className="h-4 w-4" />
                    Last allocation: {new Date(resource.allocationDate).toLocaleDateString()}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  {resource.status === 'available' ? (
                    <button
                      onClick={() => handleShowAllocation(resource)}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      Allocate
                    </button>
                  ) : resource.status === 'allocated' ? (
                    <button
                      onClick={() => handleDeallocate(resource)}
                      className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                    >
                      Return
                    </button>
                  ) : null}
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(resource)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(resource.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredResources.length === 0 && (
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first resource.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Add Resource
          </button>
        </div>
      )}

      {/* Resource Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-semibold">
                  {editingResource ? 'Edit Resource' : 'Add Resource'}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Resource Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(RESOURCE_TYPES).map(([key, type]) => (
                        <option key={key} value={key}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(RESOURCE_STATUS).map(([key, status]) => (
                        <option key={key} value={key}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                    <select
                      value={formData.condition}
                      onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(CONDITION_LEVELS).map(([key, condition]) => (
                        <option key={key} value={key}>{condition.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Asset Tag</label>
                    <input
                      type="text"
                      value={formData.assetTag}
                      onChange={(e) => setFormData(prev => ({ ...prev, assetTag: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                    <input
                      type="text"
                      value={formData.serialNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Device Details */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Device Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                      <input
                        type="text"
                        value={formData.model}
                        onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specifications</label>
                    <textarea
                      value={formData.specifications}
                      onChange={(e) => setFormData(prev => ({ ...prev, specifications: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Technical specifications, features, etc."
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Location & Allocation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Site</label>
                      <select
                        value={formData.currentSite}
                        onChange={(e) => setFormData(prev => ({ ...prev, currentSite: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Site</option>
                        {sites.map(site => (
                          <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                      </select>
                    </div>

                    {formData.status === 'allocated' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Allocated To</label>
                        <input
                          type="text"
                          value={formData.allocatedTo}
                          onChange={(e) => setFormData(prev => ({ ...prev, allocatedTo: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Information */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                      <input
                        type="date"
                        value={formData.purchaseDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price (R)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.purchasePrice}
                        onChange={(e) => setFormData(prev => ({ ...prev, purchasePrice: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Value (R)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.currentValue}
                        onChange={(e) => setFormData(prev => ({ ...prev, currentValue: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Resource Images</h4>
                  
                  <FileUpload
                    onFileSelect={handleImageUpload}
                    accept="image/*"
                    multiple
                    maxSize={5}
                    className="mb-4"
                  >
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                      <Package className="h-8 w-8 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Upload Resource Images</p>
                      <p className="text-sm text-gray-500">Drag & drop or click to select multiple images</p>
                    </div>
                  </FileUpload>

                  {formData.images && formData.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={image.url} 
                            alt={`Resource ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index)
                            }))}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Additional notes, maintenance history, or special instructions..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {editingResource ? 'Update Resource' : 'Add Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Allocation Form Modal */}
      {showAllocationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <form onSubmit={handleAllocate}>
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-semibold">
                  Allocate Resource: {allocatingResource?.name}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseAllocationForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Allocate To *</label>
                    <select
                      required
                      value={allocationData.allocatedTo}
                      onChange={(e) => setAllocationData(prev => ({ ...prev, allocatedTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Person</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.name}>{member.name} - {member.title}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Allocated By</label>
                    <input
                      type="text"
                      value={allocationData.allocatedBy}
                      onChange={(e) => setAllocationData(prev => ({ ...prev, allocatedBy: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Location</label>
                    <select
                      value={allocationData.newSite}
                      onChange={(e) => setAllocationData(prev => ({ ...prev, newSite: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Site</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.name}>{site.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Allocation Date</label>
                    <input
                      type="date"
                      value={allocationData.allocationDate}
                      onChange={(e) => setAllocationData(prev => ({ ...prev, allocationDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Allocation Type</label>
                  <select
                    value={allocationData.allocationType}
                    onChange={(e) => setAllocationData(prev => ({ ...prev, allocationType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="permanent">Permanent Assignment</option>
                    <option value="temporary">Temporary Assignment</option>
                    <option value="loan">Loan/Checkout</option>
                  </select>
                </div>

                {allocationData.allocationType !== 'permanent' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Return Date</label>
                    <input
                      type="date"
                      value={allocationData.returnDate}
                      onChange={(e) => setAllocationData(prev => ({ ...prev, returnDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={allocationData.allocationNotes}
                    onChange={(e) => setAllocationData(prev => ({ ...prev, allocationNotes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Allocation notes, conditions, or special instructions..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseAllocationForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  Allocate Resource
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};